// Shared profile-completion checks — single source of truth used by the
// Dashboard "Start Here" banner AND the parent forced-completion gate.
// Each check: { key, done: boolean, label }.
export function getProfileChecks(user, p) {
  p = p || {};
  if (!user) return [];

  if (user.user_type === 'parent') {
    const children = Array.isArray(p.children) ? p.children : [];
    const checks = [
      { key: 'name', done: Boolean(user.name), label: 'Your name' },
      { key: 'phone', done: Boolean(p.phone), label: 'Phone number' },
      { key: 'city', done: Boolean(p.city && p.province_state), label: 'City & Province/State' },
      { key: 'neighborhood', done: Boolean(p.neighborhood), label: 'Neighborhood' },
      { key: 'country', done: Boolean(p.country), label: 'Country' },
      { key: 'care', done: Boolean(p.care_type || (p.preferred_days && p.preferred_days.length)), label: 'Care preferences' },
      { key: 'children', done: children.length > 0, label: 'Add at least 1 child' },
    ];
    // Per-child, per-field checks so the completeness bar lists EXACTLY what's
    // missing for each child (e.g. "Sophie: last name") instead of one lumped line.
    children.forEach((c, idx) => {
      const fullName = ((c.full_name && String(c.full_name).trim()) || [c.name, c.last_name].filter(Boolean).join(' ').trim());
      const parts = fullName.split(/\s+/).filter(Boolean);
      const who = parts[0] || `Child ${idx + 1}`;
      checks.push(
        { key: `child-${idx}-first`, done: parts.length >= 1, label: `${parts.length >= 1 ? who : `Child ${idx + 1}`}: first name` },
        { key: `child-${idx}-last`, done: parts.length >= 2, label: `${who}: last name` },
        { key: `child-${idx}-birthday`, done: Boolean(c.birthday), label: `${who}: birthday` },
        { key: `child-${idx}-gender`, done: Boolean(c.gender), label: `${who}: gender` },
        { key: `child-${idx}-special`, done: Boolean(c.special_needs && String(c.special_needs).trim()), label: `${who}: special needs (put "None" if not applicable)` },
        { key: `child-${idx}-allergies`, done: Boolean(c.allergies && String(c.allergies).trim()), label: `${who}: allergies (put "None" if not applicable)` },
      );
    });
    return checks;
  }

  if (user.user_type === 'provider') {
    const checks = [
      { key: 'name', done: Boolean(p.daycare_name), label: 'Business name' },
      { key: 'type', done: Boolean(p.provider_type), label: 'Type of provider' },
      { key: 'address', done: Boolean(p.address && p.city), label: 'Address & City' },
      { key: 'neighborhood', done: Boolean(p.neighborhood), label: 'Neighborhood' },
      { key: 'country', done: Boolean(p.country), label: 'Country' },
      { key: 'phone', done: Boolean(p.phone), label: 'Phone number' },
      { key: 'hours', done: Boolean(p.start_time && p.end_time), label: 'Hours of operation' },
      { key: 'fees', done: Boolean(p.fee_full_time || p.fee_part_time || p.fee_drop_in), label: 'At least 1 fee type' },
      { key: 'capacity', done: Boolean(p.capacity || p.spots_available), label: 'Capacity / spots' },
      { key: 'age', done: Boolean(p.age_range_min != null || p.age_range_max != null), label: 'Age range' },
      { key: 'bio', done: Boolean(p.bio || p.description), label: 'Description / bio' },
    ];
    if (p.licensed) checks.push({ key: 'license_photo', done: Boolean(p.license_photo_url), label: 'License photo' });
    return checks;
  }

  if (user.user_type === 'worker') {
    return [
      { key: 'title', done: Boolean(p.title), label: 'Title / role' },
      { key: 'city', done: Boolean(p.city), label: 'City' },
      { key: 'neighborhood', done: Boolean(p.neighborhood), label: 'Neighborhood' },
      { key: 'country', done: Boolean(p.country), label: 'Country' },
      { key: 'phone', done: Boolean(p.phone), label: 'Phone number' },
      { key: 'experience', done: Boolean(p.experience_years != null), label: 'Experience years' },
      { key: 'certs', done: Boolean((p.certifications && p.certifications.length) || p.education), label: 'Certification or education' },
      { key: 'avail', done: Boolean(p.available_days && p.available_days.length), label: 'Availability (days & times)' },
      { key: 'bio', done: Boolean(p.bio), label: 'Bio' },
    ];
  }

  if (user.user_type === 'camp') {
    return [
      { key: 'uname', done: Boolean(user.name), label: 'Name' },
      { key: 'name', done: Boolean(p.camp_name), label: 'Camp/Program name' },
      { key: 'type', done: Boolean(p.camp_type), label: 'Type of camp' },
      { key: 'desc', done: Boolean(p.description || p.bio), label: 'Description' },
      { key: 'city', done: Boolean(p.city), label: 'City' },
      { key: 'province', done: Boolean(p.province_state), label: 'Province/State' },
      { key: 'phone', done: Boolean(p.phone), label: 'Phone number' },
      { key: 'dates', done: Boolean(p.start_date || p.program_start_dates), label: 'Program start date' },
      { key: 'age', done: Boolean(p.age_range_min != null || p.age_range_max != null), label: 'Age range' },
    ];
  }

  return [];
}
